import { useMemo } from 'react';
import cn from 'classnames';
import { Cell, ClickableIcon, IconArrowDown, IconArrowRight, VStack } from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import { type BaseRowData } from '@adv-frontend/data-provider';
import { type ColumnCellTemplateProps } from '@adv-frontend/grid';
import { type GridRowExtended } from '../../lib/types';
import styles from './CellExpand.module.css';

export type CellExpandProps<RowData extends BaseRowData = GridRowExtended> = ColumnCellTemplateProps<RowData> &
    TestProps & {
        leftPadding?: boolean;
    };

export const CellExpand = <RowData extends BaseRowData = GridRowExtended>({
    'data-testid': dataTestId = 'CellExpand',
    row,
    leftPadding = false,
}: CellExpandProps<RowData>) => {
    const { depth, getParentRow, index, getCanExpand, getIsExpanded, getToggleExpandedHandler } = row;

    const isLast = useMemo(() => {
        if (depth === 0) {
            return false;
        }

        const parent = getParentRow();

        if (!parent) {
            return false;
        }

        return index === parent.subRows.length - 1;
    }, [depth, getParentRow, index]);

    const before = useMemo(() => {
        const arr = [];

        for (let i = 0; i < depth; i++) {
            arr.push(
                <div
                    key={i}
                    className={cn(styles.space, {
                        [styles.nextline]: !isLast,
                        [styles.arrow]: isLast,
                    })}
                />
            );
        }

        return arr;
    }, [depth, isLast]);

    const isExpanded = getIsExpanded();

    return (
        <Cell
            className={styles.cell}
            leftPadding={leftPadding}
            rightPadding={false}
            topPadding={depth === 0}
            bottomPadding={false}
        >
            {before}
            {getCanExpand() && (
                <VStack className={cn(styles.expander, { [styles.opened]: isExpanded })} justifyContent="flex-start">
                    <ClickableIcon
                        data-testid={`${dataTestId}.CollapseButton`}
                        onClick={getToggleExpandedHandler()}
                        clickableAreaSize="20"
                    >
                        {isExpanded ? (
                            <IconArrowDown color="strict-black" role="supplementary" />
                        ) : (
                            <IconArrowRight color="strict-black" role="supplementary" />
                        )}
                    </ClickableIcon>
                </VStack>
            )}
        </Cell>
    );
};
